<template>
    <div id="pickup-page">
        <v-card class="search-container" :loading="inFlight">
            <div class="search-container__inputs">
                <!-- Customer ID -->
                <ClInput
                    id="id"
                    ref="id"
                    v-model.trim="externalRef"
                    label="Recipient ID"
                    autofocus
                    @keydown.enter.native="searchButtonHandler()"
                />
                <!-- Recipient -->
                <AutocompleteSuggestCustomers
                    id="customerName"
                    ref="customer"
                    label="Recipient Name"
                    :value="customer"
                    @input="( customer ) => setCustomerName( customer )"
                />
            </div>
            <!-- Search and Clear buttons -->
            <div class="search-container__actions">
                <ClButton
                    @click="clearButtonHandler()"
                >
                    Clear
                </ClButton>
                <ClButton
                    @click="searchButtonHandler()"
                >
                    Search
                </ClButton>
            </div>
        </v-card>
    </div>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import ErrorUtil from '@/utils/ErrorUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';

// Components
import AutocompleteSuggestCustomers from '@/components/AutocompleteSuggestCustomers';

// Defaults
const defaults = {
    models: () => ( {
        detailParams: {
            customerId: null,
            fullName: null,
            address1: null,
            address2: null,
            externalRef: null,
            itemsList: [],
        },
    } ),
};

export default {
    name: 'MailPickUp',
    components: {
        AutocompleteSuggestCustomers,
    },
    data() {
        return {
            models: defaults.models(),
            inFlight: false,
            recipientRefreshKey: 0,
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            pickupFields: ( state ) => state.Mail.Pickup.fields,
        } ),
        hasItems() {
            return this.models.detailParams.itemsList.length > 0;
        },
        customer: {
            get() {
                return this.pickupFields.customer;
            },
            set( customer ) {
                this.$store.commit( 'Mail/Pickup/SET_FIELDS_CUSTOMER', { customer } );
            },
        },
        externalRef: {
            get() {
                return this.pickupFields.externalRef;
            },
            set( externalRef ) {
                this.$store.commit( 'Mail/Pickup/SET_FIELDS_EXTERNAL_REF', { externalRef } );
            },
        },
    },
    async mounted() {
        await this.$store.dispatch( 'Mail/Settings/get' );
        this.clearButtonHandler();
    },
    beforeDestroy() {
        this.$store.dispatch( 'Mail/Pickup/reset' );
    },
    methods: {
        setCustomerName( customer ) {
            this.$store.commit( 'Mail/Pickup/SET_FIELDS_CUSTOMER', { customer } );
            // Check if we have the externalRef
            if ( customer && customer.externalRef ) {
                this.$store.commit( 'Mail/Pickup/SET_FIELDS_EXTERNAL_REF', { externalRef: customer.externalRef } );
            }
            if ( customer ) this.searchButtonHandler();
        },
        async searchButtonHandler() {
            this.inFlight = true;
            // Get the customer info by name or student id
            try {
                const { data } = await LogisticsApi.getCustomerByNameOrId( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    externalRef: this.externalRef || null,
                    customerName: ( this.customer && this.customer.name )
                        ? this.customer.name
                        : '',
                } );
                if ( data.customerSearchDTO && data.customerSearchDTO[ 0 ] ) {
                    this.models.detailParams.customerId = data.customerSearchDTO[ 0 ].customerId;
                    this.models.detailParams.fullName = data.customerSearchDTO[ 0 ].fullName;
                    this.models.detailParams.address1 = data.customerSearchDTO[ 0 ].addressLine1 || '';
                    this.models.detailParams.address2 = data.customerSearchDTO[ 0 ].addressLine2 || '';
                    this.models.detailParams.externalRef = data.customerSearchDTO[ 0 ].externalRef;
                } else {
                    await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                    await this.$store.dispatch( 'snackbar', {
                        message: 'Recipient not found.',
                        type: 'error',
                        duration: 5000,
                    }, { root: true } );
                }
            } catch ( error ) {
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                await this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
            } finally {
                this.inFlight = false;
                if ( this.models.detailParams.customerId > 0 ) {
                    this.getCustomerPickupList();
                }
            }
        },
        async getCustomerPickupList() {
            this.inFlight = true;
            try {
                const { data } = await LogisticsApi.getCustomerPickupList( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    customerId: this.models.detailParams.customerId,
                } );
                this.models.detailParams.itemsList = data.mailSearchListDTO
                    ? data.mailSearchListDTO.map( ( pickup ) => ( {
                        ...pickup,
                        scanned: false,
                    } ) )
                    : [];
                if ( !this.hasItems ) {
                    await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                    await this.$store.dispatch( 'snackbar', {
                        message: 'No items to be picked up',
                        type: 'error',
                        duration: 5000,
                    }, { root: true } );
                }
            } catch ( error ) {
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                await this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                }, { root: true } );
            } finally {
                this.inFlight = false;
                if ( this.hasItems ) {
                    await this.$router.push( {
                        name: 'MailPickupDetail',
                        params: {
                            id: this.models.detailParams.customerId,
                            detailParams: this.models.detailParams,
                        },
                    } );
                }
            }
        },
        clearButtonHandler() {
            this.$store.dispatch( 'Mail/Pickup/reset' );
            this.models = defaults.models();
            this.customer = null;
        },
    },
};
</script>

<style lang="scss" scoped>
#pickup-page {
    display: flex;
    justify-content: center;
    padding: 1rem 0;
}
.search-container {
    width: 100%;
    max-width: 62.5rem;

    &__inputs {
        display: grid;
        column-gap: 1rem;
        row-gap: 1rem;
        margin: 1.5rem;

        @include mixins-above(tablet) {
            grid-template-columns: 1fr 1fr;
        }
    }
    &__actions {
        display: flex;
        justify-content: flex-end;
        margin: 1.5rem;
        margin-top: 0rem;
        & > * {
            margin-left: 1rem;
        }
    }
}
</style>
